import React from "react";
import {
  Alert,
  Box,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  SelectChangeEvent,
  TextField,
} from "@mui/material";
import {
  WORKFLOW_EMAIL_RECIPIENT_SOURCES,
  WorkflowEmailConfig,
  WorkflowEmailRecipientSource,
} from "./workflowBuilderData";
import { useWorkflowReadOnly } from "./WorkflowReadOnlyContext";

/** The prototype's Email wording, verbatim. */
const EMAIL_DISCLAIMER =
  "Enterprise vs Portal: a manager / supervisor is resolved from Enterprise; “Creator” is the Portal user who started the case.";

interface WorkflowEmailPanelProps {
  config: WorkflowEmailConfig;
  onChange: (config: WorkflowEmailConfig) => void;
}

const WorkflowEmailPanel: React.FC<WorkflowEmailPanelProps> = ({ config, onChange }) => {
  const viewOnly = useWorkflowReadOnly();
  const dynamicRecipient = config.recipientSource !== "specific-user";

  return (
    <Box className="workflow-canvas-dialog__config-panel">
      <Box className="workflow-canvas-dialog__config-grid">
        <FormControl disabled={viewOnly} size="small" fullWidth>
          <InputLabel id="email-recipient-source-label">Send to</InputLabel>
          <Select
            labelId="email-recipient-source-label"
            label="Send to"
            value={config.recipientSource}
            onChange={(event: SelectChangeEvent) =>
              // An address and a field reference aren't interchangeable, so a switch clears it.
              onChange({
                ...config,
                recipientSource: event.target.value as WorkflowEmailRecipientSource,
                recipient: "",
              })
            }
          >
            {WORKFLOW_EMAIL_RECIPIENT_SOURCES.map((source) => (
              <MenuItem key={source.value} value={source.value}>
                {source.label}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        <TextField
          disabled={viewOnly}
          label={dynamicRecipient ? "Reference" : "Recipient"}
          size="small"
          value={config.recipient}
          onChange={(event) => onChange({ ...config, recipient: event.target.value })}
          placeholder={dynamicRecipient ? "Field the person comes from" : "user@hamaspik.org"}
        />
      </Box>

      {dynamicRecipient && (
        <Alert severity="info" className="workflow-canvas-dialog__disclaimer">
          {EMAIL_DISCLAIMER}
        </Alert>
      )}

      <TextField
        disabled={viewOnly}
        label="Subject"
        size="small"
        fullWidth
        value={config.subject}
        onChange={(event) => onChange({ ...config, subject: event.target.value })}
      />
      <TextField
        disabled={viewOnly}
        label="Body"
        size="small"
        fullWidth
        multiline
        minRows={6}
        value={config.body}
        onChange={(event) => onChange({ ...config, body: event.target.value })}
        helperText="Use {{variable}} tokens to insert workflow values."
      />
      <TextField
        disabled={viewOnly}
        label="Attach"
        size="small"
        fullWidth
        value={config.attachment}
        onChange={(event) => onChange({ ...config, attachment: event.target.value })}
        placeholder="e.g. Executed PIF (PDF)"
      />
    </Box>
  );
};

export default WorkflowEmailPanel;
